<template>
  <div class="heatmap-wrapper">
    <div class="heatmap-header">
      <p class="heatmap-title">{{ $t('generalSatisfaction') }}</p>
    </div>
    <div class="heatmap-options">
      <p
        :class="{ active: type === 'satisfaction' }"
        @click="chooseOption('satisfaction')"
      >
        {{ $t('customersSatisfaction') }}
      </p>
      <p :class="{ active: type === 'amoun' }" @click="chooseOption('amoun')">
        {{ $t('customersAmount') }}
      </p>
    </div>
    <div class="heatmap-chart">
      <v-chart class="chart" :option="chartOption" autoresize />
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { HeatmapChart as EChartsHeatmap } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  VisualMapComponent
} from 'echarts/components';
import VChart from 'vue-echarts';

import { colorScale, grayScale } from './configs';
import { HeatmapDayData, HeatmapInput } from '@/store/modules/orders/types';
import { CustomersStats, SpacedDate } from '@/store/modules/pharmacy/types';

use([
  CanvasRenderer,
  EChartsHeatmap,
  GridComponent,
  TooltipComponent,
  VisualMapComponent
]);

type HeatmapPoint = {
  value: [number, number, number];
  itemStyle: { color: string };
};

@Options({
  name: 'HeatmapChart',
  components: { VChart },
  props: {
    heatmapData: {
      type: [Array, Object] as unknown as () => HeatmapInput,
      required: true
    }
  },
  data() {
    return {
      type: 'satisfaction'
    };
  },
  methods: {
    chooseOption(type: string) {
      this.type = type;
    }
  }
})
/* eslint-disable */
export default class HeatmapChart extends Vue {
  heatmapData!: Record<string, number[]> | HeatmapDayData[];
  type!: string;
  colorScale = colorScale;
  grayScale = grayScale;

  get maxAmountValue(): number {
    if (this.type !== 'amoun') return 1;

    const data = Object.values(
      (this.heatmapData as unknown as CustomersStats).customersAmount || {}
    );

    let max = 0;

    data.forEach((arr) => {
      if (Array.isArray(arr)) {
        arr.forEach((v: any) => {
          if (typeof v === 'number' && v > max) max = v;
        });
      }
    });

    return max || 1;
  }

  getColorForValue(v: number): string {
    if (v === -1) return '#E5E5E5';

    // Satisfaction — працюємо напряму з %
    if (this.type === 'satisfaction') {
      if (v === 0) return '#D62828';

      const index = Math.min(
        this.colorScale.length - 1,
        Math.floor((v / 100) * this.colorScale.length)
      );

      return this.colorScale[index];
    }

    // Amount — нормуємо до 100%
    const percent = (v / this.maxAmountValue) * 100;

    const idx = Math.min(
      this.grayScale.length - 1,
      Math.floor((percent / 100) * this.grayScale.length)
    );

    return this.grayScale[idx];
  }

  get currentData(): Record<string, (string | number)[]> {
    if (!this.heatmapData) return {};

    return this.type === 'satisfaction'
      ? (this.heatmapData as unknown as CustomersStats).customersSatisfaction ||
          {}
      : (this.heatmapData as unknown as CustomersStats).customersAmount || {};
  }

  get rows(): HeatmapDayData[] {
    const src = this.currentData;

    return Object.entries(src).map(([date, values]) => {
      const arr = Array.isArray(values) ? values : [];

      return {
        date,
        values: arr
          .map((v: any) => {
            if (v === null || v === undefined) return -1;
            if (typeof v === 'string') return Number(v.replace('%', ''));
            return v;
          })
          .slice(0, 24)
          .concat(Array(24).fill(-1))
          .slice(0, 24)
      };
    });
  }

  get chartOption() {
    if (!this.rows.length) return {};

    const dates = this.rows.map((d) => d.date);
    const hours = Array.from(
      { length: 24 },
      (_, i) => `${i.toString().padStart(2, '0')}:00`
    );

    const spacedHours: string[] = [];
    hours.forEach((h, i) => {
      spacedHours.push(h);
      if (i < hours.length - 1) spacedHours.push('');
    });

    const spacedDates: string[] = [];
    dates.forEach((d, i) => {
      spacedDates.push(d);
      if (i < dates.length - 1) spacedDates.push('');
    });

    const chartData: HeatmapPoint[] = [];
    this.rows.forEach((day, y) => {
      day.values.forEach((v, x) => {
        chartData.push({
          value: [x * 2, y * 2, v],
          itemStyle: { color: this.getColorForValue(v) }
        });
      });
    });

    return {
      tooltip: {
        position: 'top',
        backgroundColor: '#fff',
        borderColor: '#ddd',
        borderWidth: 1,
        textStyle: { color: '#333', fontSize: 12 },
        formatter: (p: SpacedDate) => {
          const date = spacedDates[p.value[1]] || '';
          const hour = spacedHours[p.value[0]] || '';
          let v = p.value[2];

          if (v === -1) return `${date}<br>${hour} — <b>-</b>`;

          const text = this.type === 'satisfaction' ? `${v}%` : `${v}`;

          return `${date}<br>${hour} — <b>${text}</b>`;
        }
      },
      grid: {
        top: 32,
        bottom: 0,
        left: 8,
        right: 8,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: spacedHours,
        axisLabel: {
          color: '#666',
          fontSize: 10,
          rotate: 45,
          interval: (i: number) => i % 2 === 0
        },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'category',
        data: spacedDates,
        axisLabel: {
          color: '#444',
          fontSize: 10,
          interval: (i: number) => i % 2 === 0
        },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      visualMap: {
        show: false,
        min: 0,
        max: 100,
        inRange: {
          color: this.type === 'satisfaction' ? this.colorScale : this.grayScale
        }
      },
      series: [
        {
          type: 'heatmap',
          data: chartData,
          label: { show: false },
          itemStyle: { borderRadius: 16 },
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(0,0,0,0.3)'
            }
          }
        }
      ]
    };
  }
}
</script>

<style lang="scss" scoped>
.heatmap-wrapper {
  position: relative;
  .heatmap-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    .heatmap-title {
      font-weight: 600;
      font-size: 16px;
    }
  }
  .heatmap-options {
    position: absolute;
    z-index: 1;
    top: 4%;
    right: 0;
    transform: translate(-4%, -50%);
    display: flex;
    p {
      font-size: $fourteenSize;
      color: $lineColor;
      border: 1px solid $border;
      border-radius: 4px;
      padding: 8px;
      cursor: pointer;
      &:first-child {
        margin-right: 12px;
      }
      &:hover {
        color: $positive;
        border-color: $positive;
      }
      &.active {
        border-color: $lineColor;
        color: $lineColor;
      }
    }
  }
  .heatmap-chart {
    width: 100%;
    height: 420px;
  }
}
</style>
